<template>
  <div
      v-if="showLoader"
      class="fixed inset-0 flex items-center justify-center bg-gray-500 bg-opacity-50 z-50"
  >
    <svg
        class="animate-spin h-16 w-16 text-white"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
    >
      <circle
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          stroke-width="4"
          fill="none"
      />
    </svg>
  </div>
  <div v-else-if="company">
    <div class="my-4 flex flex-col md:flex-row md:items-start space-y-2 md:justify-between md:space-y-0 w-full p-4">
      <div class="w-3/4 flex space-x-4">
        <div class="flex flex-col space-y-2">
          <div class="text-lg font-semibold">Company Name</div>
          <span class="text-base">{{ company.company_name }}</span>
        </div>
        <div class="flex flex-col space-y-2">
          <div class="text-lg font-semibold">No. Of Beneficiaries</div>
          <span class="text-base">{{ company.beneficiaries }}</span>
        </div>
        <div class="flex flex-col space-y-2">
          <div class="text-lg font-semibold">Added On</div>
          <span class="text-base">
            {{
              new Date(company.created_at).toLocaleDateString('en-US', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit'
              })
            }}
          </span>
        </div>
      </div>
      <div class="w-full flex justify-end space-x-2">
        <CustomButton
          label="Add Beneficiary"
          :icon-path="'M5 12h14m-7 7V5'"
          bg-class="bg-custom-yellow"
          @click="openAddBeneficiaryModal"
        />
        <CustomButton
          label="Upload Beneficiaries"
          :icon-path="'M5 12h14m-7 7V5'"
          bg-class="bg-custom-yellow"
          @click="openModal"
        />
      </div>
    </div>
    <div class="relative overflow-x-auto shadow-md sm:rounded-sm mt-5">
      <table class="w-full text-left rtl:text-right text-gray-700">
        <thead>
          <tr>
            <td
              v-for="(header, index) in tableHeaders"
              :key="index"
              scope="col"
              class="px-6 py-3 border-b"
            >
              <div class="flex items-center font-semibold text-base">
                {{ header }}
              </div>
            </td>
          </tr>
        </thead>
        <tbody v-if="beneficiaries.length > 0">
          <tr
            v-for="(beneficiary, index) in beneficiaries"
            :key="index"
            class="border-b text-base"
          >
            <td class="px-6 py-2">{{ beneficiary.name }}</td>
            <td class="px-6 py-2">{{ beneficiary.id_number }}</td>
            <td class="px-6 py-2">KES {{ beneficiary.cover_amount }}</td>
            <td class="px-6 py-2">
              {{
                new Date(beneficiary.validity_end_date).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit'
                })
              }}
            </td>
            <td class="px-6 py-2">
              {{
                new Date(beneficiary.created_at).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit'
                })
              }}
            </td>
            <td class="px-6 py-2">
              {{
                new Date(beneficiary.updated_at).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit'
                })
              }}
            </td>
            <td class="px-6 py-2">
              <div class="flex space-x-3">
                <button type="button" @click="openEditBeneficiaryModal(beneficiary)">
                  <SquarePen stroke="#012169" fill="white" size="24" />
                </button>
                <button type="button" @click="triggerDeleteBeneficiary(beneficiary.id)">
                  <Trash2 stroke="red" fill="none" size="24"/>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
        <tbody v-else>
          <tr>
            <td colspan="7" class="px-6 py-4 text-center text-gray-500">
              No beneficiaries found.
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
  <div v-else class="flex items-center justify-center h-full">
    <p class="text-gray-500">Failed to load company details.</p>
  </div>

  <AddCompanyBeneficiariesModal
    :show="showAddBeneficiaryModal"
    :companyId="company?.id"
    @close="closeAddBeneficiaryModal"
    @add-success="fetchCompanyDetails"
  />

  <UploadCompanyBeneficiariesModal
    :show="showUploadCompanyBeneficiariesModal"
    :companyId="company?.id"
    @close="closeModal"
    @upload-success="fetchCompanyDetails"
  />

  <EditBeneficiaryModal
    :show="showEditBeneficiaryModal"
    @close="closeEditBeneficiaryModal"
    :beneficiary="selectedBeneficiary"
    @updated="fetchCompanyDetails"
  />

  <ConfirmationModal
    :show="showDeleteConfirmationModal"
    :title="'Delete Beneficiary'"
    :message="'Are you sure you want to delete this beneficiary?'"
    @confirm="deleteBeneficiary"
    @cancel="closeDeleteConfirmationModal"
  />
</template>

<script setup>
import { onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useToast } from 'vue-toast-notification'
import { SquarePen, Trash2 } from 'lucide-vue-next'
import CustomButton from "@/components/CustomButton.vue"
import UploadCompanyBeneficiariesModal from "@/views/modals/ffs-management/UploadCompanyBeneficiariesModal.vue"
import AddCompanyBeneficiariesModal from "@/views/modals/ffs-management/AddCompanyBeneficiariesModal.vue"
import EditBeneficiaryModal from '@/views/modals/ffs-management/EditBeneficiaryModal.vue'
import ConfirmationModal from "@/views/modals/shared/ConfirmationModal.vue";

import apiClient from '@/services/api'

const route = useRoute()
const toast = useToast()

const showLoader = ref(false)
const beneficiaryDeleteLoading = ref(false)
const company = ref(null)
const beneficiaries = ref([])
const selectedBeneficiary = ref(null)
const deletingBeneficiaryId = ref(null)
const showUploadCompanyBeneficiariesModal = ref(false)
const showAddBeneficiaryModal = ref(false)
const showEditBeneficiaryModal = ref(false)
const showDeleteConfirmationModal = ref(false)

// Pagination reactive data properties
const currentPage = ref(1)
const itemsPerPage = ref(10)
const totalItems = ref(null)
const totalPages = ref(null)

const tableHeaders = [
  'Beneficiary',
  'ID Number',
  'Remaining Cover Amount',
  'Valid Until',
  'Added On',
  'Updated On',
  'Actions'
]

const fetchCompanyDetails = async () => {
  try {
    showLoader.value = true

    const response = await apiClient.fetchCompanyWithBeneficiaries(route.params.id, {
      page: currentPage.value,
      per_page: itemsPerPage.value
    });

    console.log('fetched company', response)

    if (response.success) {
      company.value = response.data.company
      beneficiaries.value = response.data.beneficiaries
      totalItems.value = response.meta.total_items
      currentPage.value = response.meta.current_page
      totalPages.value = response.meta.total_pages
      itemsPerPage.value = response.meta.per_page
    }
    toast.success('Company Details fetched successfully.')
  } catch (error) {
    toast.error(error.message || 'Failed to fetch Company Details.')
  } finally {
    showLoader.value = false
  }
}

onMounted(() => {
  fetchCompanyDetails()
})

const openModal = () => {
  showUploadCompanyBeneficiariesModal.value = true
}

const closeModal = () => {
  showUploadCompanyBeneficiariesModal.value = false
}

const openAddBeneficiaryModal = () => {
  showAddBeneficiaryModal.value = true
}

const closeAddBeneficiaryModal = () => {
  showAddBeneficiaryModal.value = false
}

const openEditBeneficiaryModal = (beneficiary) => {
  showEditBeneficiaryModal.value = true
  selectedBeneficiary.value = beneficiary
}

const closeEditBeneficiaryModal = () => {
  showEditBeneficiaryModal.value = false
}

const closeDeleteConfirmationModal = () => {
  showDeleteConfirmationModal.value = false
  deletingBeneficiaryId.value = null
}

const triggerDeleteBeneficiary = (beneficiaryId) => {
  deletingBeneficiaryId.value = beneficiaryId
  showDeleteConfirmationModal.value = true
}

const deleteBeneficiary = async () => {
  try {
    beneficiaryDeleteLoading.value = true
    let response = await apiClient.deleteScheme(deletingBeneficiaryId.value)
    if (response.success) {
      fetchCompanyDetails()
      toast.success("Beneficiary deleted successfully")
      showDeleteConfirmationModal.value = false
    } else {
      showDeleteConfirmationModal.value = false
      toast.error(response.message || "Failed to delete beneficiary")
    }
  } catch (error) {
    showDeleteConfirmationModal.value = false
    toast.error(error.message || "Failed to delete beneficiary")
  } finally {
    beneficiaryDeleteLoading.value = false
  }
}
</script>